import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SponsorPlaceholder from "../components/SponsorPlaceholder";
import ScrollToTop from "../components/ScrollToTop";
import { colors } from "../styles/colors";

export default function ClausesDirectory() {
  const employmentClauses = [
    { path: "/explain-fixed-term-employment-clause", title: "Fixed Term Employment" },
    { path: "/explain-termination-for-cause-clause", title: "Termination for Cause" },
    { path: "/explain-notice-period-clause", title: "Notice Period" },
    { path: "/explain-non-compete-clause", title: "Non-Compete" },
    { path: "/explain-probation-period-clause", title: "Probation Period" },
    { path: "/explain-payment-in-lieu-of-notice-clause", title: "Payment in Lieu of Notice" },
    { path: "/explain-restraint-of-trade-clause", title: "Restraint of Trade" },
  ];

  const generalClauses = [
    { path: "/explain-confidentiality-clause", title: "Confidentiality" },
    { path: "/explain-intellectual-property-clause", title: "Intellectual Property" },
    { path: "/explain-indemnity-clause", title: "Indemnity" },
    { path: "/explain-cancellation-clause", title: "Cancellation" },
    { path: "/explain-force-majeure-clause", title: "Force Majeure" },
    { path: "/explain-limitation-of-liability-clause", title: "Limitation of Liability" },
    { path: "/explain-variation-clause", title: "Variation" },
    { path: "/explain-governing-law-clause", title: "Governing Law" },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <header 
        className="px-6 md:px-12 lg:px-24 py-6" 
        style={{ borderBottom: `1px solid ${colors.border}` }}
      >
        <Link 
          to="/" 
          data-testid="home-link" 
          className="inline-flex items-center gap-3 hover:opacity-80 transition-opacity"
        >
          <img 
            src="/clauseclear-logo.png" 
            alt="ClauseClear" 
            className="h-10 object-contain"
          />
        </Link>
      </header>

      <article className="px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-4xl mx-auto">
          <h1 
            data-testid="page-title" 
            className="text-4xl md:text-5xl font-medium tracking-tight leading-[1.1] mb-8" 
            style={{ color: colors.text }}
          >
            Browse Contract Clauses in Plain English
          </h1>

          <p className="text-base md:text-lg leading-relaxed mb-12" style={{ color: colors.textMuted }}>
            Select any clause below to see a plain-English explanation of what it means, why it matters, and how it typically affects you. No legal jargon, no confusing terms—just clear, helpful information.
          </p>

          {/* Hub Links */}
          <section className="mb-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Link
                to="/clauses/employment"
                className="p-8 rounded-xl transition-all"
                style={{ 
                  backgroundColor: colors.backgroundLight, 
                  border: `2px solid ${colors.accent}` 
                }}
              >
                <h2 className="text-2xl font-medium mb-3" style={{ color: colors.accent }}>
                  Employment Clauses
                </h2>
                <p className="text-base" style={{ color: colors.textMuted }}>
                  Understand clauses in employment contracts, from notice periods to non-competes
                </p>
              </Link>
              <Link
                to="/clauses/business"
                className="p-8 rounded-xl transition-all"
                style={{ 
                  backgroundColor: colors.backgroundLight, 
                  border: `2px solid ${colors.accent}` 
                }}
              >
                <h2 className="text-2xl font-medium mb-3" style={{ color: colors.accent }}>
                  Business & Freelancer Clauses
                </h2>
                <p className="text-base" style={{ color: colors.textMuted }}>
                  Explore clauses common in business contracts, from scope of work to liability
                </p>
              </Link>
            </div>
          </section>

          {/* Employment Clauses */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-6 h-px" style={{ backgroundColor: colors.accent }} />
              <h2 className="text-2xl font-medium" style={{ color: colors.text }}>
                Employment Clauses
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {employmentClauses.map((clause) => (
                <Link
                  key={clause.path}
                  to={clause.path}
                  data-testid={`clause-link-${clause.path.split('/')[1]}`}
                  className="flex items-center justify-between p-6 rounded-xl transition-all group"
                  style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }}
                >
                  <span className="text-base font-medium" style={{ color: colors.text }}>
                    {clause.title}
                  </span>
                  <ArrowRight 
                    className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" 
                    style={{ color: colors.accent }} 
                  />
                </Link>
              ))}
            </div>
          </section>

          {/* General Contract Clauses */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-6 h-px" style={{ backgroundColor: colors.accent }} />
              <h2 className="text-2xl font-medium" style={{ color: colors.text }}>
                General Contract Clauses
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {generalClauses.map((clause) => (
                <Link
                  key={clause.path}
                  to={clause.path}
                  data-testid={`clause-link-${clause.path.split('/')[1]}`}
                  className="flex items-center justify-between p-6 rounded-xl transition-all group"
                  style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }}
                >
                  <span className="text-base font-medium" style={{ color: colors.text }}>
                    {clause.title}
                  </span>
                  <ArrowRight 
                    className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" 
                    style={{ color: colors.accent }} 
                  />
                </Link>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <div 
            data-testid="cta-section" 
            className="text-center p-8 rounded-2xl" 
            style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }}
          >
            <p className="text-lg mb-4" style={{ color: colors.textMuted }}>
              Have a specific clause you want to understand?
            </p>
            <Link
              to="/"
              data-testid="cta-button"
              className="inline-flex items-center gap-2 hover:opacity-90 rounded-full px-8 py-4 text-lg font-medium transition-all"
              style={{ backgroundColor: colors.accent, color: colors.background }}
            >
              Try ClauseClear
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          {/* Sponsorship Hub Placeholder */}
          <SponsorPlaceholder variant="hub" />
        </div>
      </article>

      <footer 
        className="px-6 md:px-12 lg:px-24 py-12 text-center" 
        style={{ borderTop: `1px solid ${colors.border}`, backgroundColor: colors.backgroundDark }}
      >
        <p className="text-sm mb-2" style={{ color: colors.textDim }}>
          ClauseClear is an educational tool. It does not provide legal advice.
        </p>
        <a 
          href="https://buymeacoffee.com/jamesmoneyz" 
          target="_blank" 
          rel="noopener noreferrer" 
          className="text-base font-medium hover:opacity-80 transition-opacity" 
          style={{ color: colors.accent }}
        >
          Support the project
        </a>
      </footer>

      <ScrollToTop />
    </div>
  );
}
